"use client"

import { useState, useEffect } from "react"
import { Grid, GridColumn } from "@progress/kendo-react-grid"
import "@progress/kendo-theme-default/dist/all.css"
import { Tooltip } from "antd"
import Select from "react-select"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"

import { PrivateAxios, url } from "../../../environment/AxiosInstance"
import { isURL } from "../../../environment/Helper"
import { UserAuth } from "../../auth/Auth"
import "./project-dashboard.css"

// Cell Renderers
const SerialNumberCell = (props) => <td>{props.dataIndex + 1}</td>

const ProjectNameCell = (props) => (
  <td>
    <p className="text-dark fw-bold mb-0">{props.dataItem.project.project_name}</p>
  </td>
)

const TaskNameCell = (props) => (
  <td>
    <p className="text-dark fw-bold mb-0">{props.dataItem.task_name}</p>
  </td>
)
const DoerSelect = ({ users = [], value = [], onChange = () => {}, placeholder = "Select doers..." }) => {
  const options = users.map((u) => ({ value: u.id, label: u.name }))
  const selected = options.filter((o) => value.includes(o.value))

  return (
    <Select
      isMulti
      options={options}
      value={selected}
      onChange={(selectedOptions) => onChange(selectedOptions ? selectedOptions.map((o) => o.value) : [])}
      placeholder={placeholder}
      classNamePrefix="react-select"
    />
  )
}

const DoerNameCell = (props, users) => {
  let doers = props.dataItem.assigned_to

  if (typeof doers === "string") {
    try {
      doers = JSON.parse(doers)
    } catch {
      doers = []
    }
  }
  if (!Array.isArray(doers)) doers = doers ? [doers] : []

  return (
    <td>
      <div className="d-flex flex-column gap-2">
        {doers.length > 0 ? (
          doers.map((doerId, index) => {
            const user = users.find((u) => u.id === doerId)
            return (
              <div className="d-flex align-items-center" key={index}>
                <div className="exp-avtar bg-exp-yellow me-2 flex-shrink-0" style={{width: '32px', height: '32px'}}>
                  {user?.user_photo != null ? (
                    <img
                      className="prof-img"
                      src={isURL(user?.user_photo) ? user.user_photo : url + user.user_photo}
                      alt="user"
                      style={{width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%'}}
                    />
                  ) : (
                    <span style={{fontSize: '12px', fontWeight: 'bold'}}>
                      {user?.name
                        ? user.name.split(" ").length > 1
                          ? user.name.split(" ")[0][0] + user.name.split(" ")[1][0]
                          : user.name.slice(0, 2)
                        : ""}
                    </span>
                  )}
                </div>
                <span className="text-dark fw-medium" style={{fontSize: '14px'}}>
                  {user?.name || `ID: ${doerId}`}
                </span>
              </div>
            )
          })
        ) : (
          <span className="text-muted">N/A</span>
        )}
      </div>
    </td>
  )
}

const AssignByCell = (props, users) => {
  const assignedBy = props.dataItem.assigned_by
  const user = users?.find((u) => u.id === assignedBy)

  return (
    <td>
      {user ? (
        <div className="d-flex align-items-center">
          <div className="exp-avtar bg-exp-yellow me-2 flex-shrink-0" style={{width: '32px', height: '32px'}}>
            {user?.user_photo != null ? (
              <img
                className="prof-img"
                src={isURL(user?.user_photo) ? user.user_photo : url + user.user_photo}
                alt="user"
                style={{width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%'}}
              />
            ) : (
              <span style={{fontSize: '12px', fontWeight: 'bold'}}>
                {user?.name
                  ? user.name.split(" ").length > 1
                    ? user.name.split(" ")[0][0] + user.name.split(" ")[1][0]
                    : user.name.slice(0, 2)
                  : ""}
              </span>
            )}
          </div>
          <span className="text-dark fw-medium" style={{fontSize: '14px'}}>
            {user.name}
          </span>
        </div>
      ) : (
        <span className="text-muted">N/A</span>
      )}
    </td>
  )
}

const DateCell = (props, field) => {
  const date = props.dataItem[field]
  if (!date)
    return (
      <td>
        <span className="text-muted">N/A</span>
      </td>
    )

  const formattedDate = new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  })

  return (
    <td>
      <span>{formattedDate}</span>
    </td>
  )
}

const DelayCell = (props) => {
  const endDate = new Date(props.dataItem.end_date)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  endDate.setHours(0, 0, 0, 0)

  const timeDiff = today - endDate

  if (timeDiff <= 0) {
    return (
      <td>
        <span className="text-success">On Time</span>
      </td>
    )
  }

  const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24))
  const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
  const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60))
  const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000)

  let delayText = ""
  if (days > 0) delayText += `${days}d `
  if (hours > 0) delayText += `${hours}h `
  if (minutes > 0) delayText += `${minutes}m `
  if (seconds > 0) delayText += `${seconds}s`

  return (
    <td>
      <span style={{ color: "#dc3545", fontWeight: "bold" }}>{delayText.trim()}</span>
    </td>
  )
}

const PriorityCell = (props) => {
  const priority = props.dataItem.priority?.toLowerCase() || "low"
  const priorityClass = `priority-set ${priority === "high" ? "high1" : priority === "medium" ? "medium" : "low"}`
  return (
    <td>
      <div className={priorityClass}>
        <i className="fas fa-flag mr-2" />
        <span>{priority.charAt(0).toUpperCase() + priority.slice(1)}</span>
      </div>
    </td>
  )
}

const StatusCell = (props) => {
  const status = props.dataItem.status || "Pending"
  const statusClass =
    status === "Completed" ? "badge-success" : status === "In Progress" ? "badge-info" : "badge-secondary"
  return (
    <td>
      <span className={`badge gth-badge-${statusClass} rounded-pill`}>{status}</span>
    </td>
  )
}

const AccuracyCell = (props) => {
  const accuracy = props.dataItem.complete_accuracy || 0
  const accuracyColor = accuracy >= 80 ? "#0f9d58" : accuracy >= 50 ? "#ff9800" : "#dc3545"
  return (
    <td>
      <div style={{ fontWeight: "bold", color: accuracyColor }}>{accuracy.toFixed(1)}%</div>
    </td>
  )
}

// Main Component
function ProjectDashboard() {
  const { User, MatchPermission } = UserAuth()
  const [allTasks, setAllTasks] = useState([])
  const [filteredTasks, setFilteredTasks] = useState([])
  const [activeTab, setActiveTab] = useState("pending")
  const [loading, setLoading] = useState(false)
  const [allUsers, setAllUsers] = useState([])

  const [filters, setFilters] = useState({
    taskName: "",
    assignees: [], // assigned_by multi-select (array of ids)
    doers: [], // assigned_to multi-select (array of ids)
    fromDate: null, // Date object
    toDate: null, // Date object
    priority: "",
  })

  // NEW: control visibility of filter/search panel
  const [showFilters, setShowFilters] = useState(false)

  const fetchAllProjectTasks = async () => {
    setLoading(true)
    try {
      const res = await PrivateAxios.get("/projecttasks/all-tasks")

      if (res.data?.success && res.data?.tasks) {
        const tasksWithAccuracy = res.data.tasks.map((task) => {
          const startDate = new Date(task.start_date)
          const endDate = new Date(task.end_date)
          const completedDate = task.completed_date ? new Date(task.completed_date) : null

          // Calculate accuracy based on completion timeline
          let accuracy = 100
          if (completedDate) {
            const plannedDuration = endDate - startDate
            const actualDuration = completedDate - startDate
            accuracy = Math.max(0, Math.min(100, 100 - ((actualDuration / plannedDuration) * 100 - 100)))
          }

          return {
            ...task,
            complete_accuracy: accuracy,
          }
        })
        const sorted = tasksWithAccuracy.sort((a, b) => a.complete_accuracy - b.complete_accuracy)
        setAllTasks(sorted)
        applyFilters(sorted, activeTab)
      }
    } catch (err) {
      console.error("Error fetching project tasks:", err)
    } finally {
      setLoading(false)
    }
  }

  const applyFilters = (tasks, tab) => {
    let filtered = tasks

    // Filter by tab status
    if (tab === "inprogress") {
      filtered = filtered.filter((task) => task.status === "In Progress")
    } else if (tab === "complete") {
      filtered = filtered.filter((task) => task.status === "Completed")
    } else if (tab === "pending") {
      filtered = filtered.filter((task) => task.status === "Pending")
    }

    // Apply other filters
    if (filters.taskName) {
      filtered = filtered.filter((task) => task.task_name?.toLowerCase().includes(filters.taskName.toLowerCase()))
    }

    // Project Name filter (supports task.project.project_name or task.project_name)
    if (filters.projectName) {
      const pname = filters.projectName.toLowerCase()
      filtered = filtered.filter((task) => {
        const projectName = (task.project && task.project.project_name) || task.project_name || ""
        return projectName.toLowerCase().includes(pname)
      })
    }

    // assigned_by multi (assignees)
    if (filters.assignees && filters.assignees.length > 0) {
      filtered = filtered.filter((task) => filters.assignees.includes(Number(task.assigned_by)))
    }

    // assigned_to multi (doers)
    if (filters.doers && filters.doers.length > 0) {
      filtered = filtered.filter((task) => {
        let assignees = task.assigned_to
        if (typeof assignees === "string") {
          try {
            assignees = JSON.parse(assignees)
          } catch {
            assignees = []
          }
        }
        if (!Array.isArray(assignees)) assignees = assignees ? [assignees] : []
        return assignees.some((a) => filters.doers.includes(Number(a)))
      })
    }

    if (filters.priority) {
      filtered = filtered.filter((task) => task.priority?.toLowerCase() === filters.priority.toLowerCase())
    }

    if (filters.fromDate) {
      const fromDate = new Date(filters.fromDate)
      fromDate.setHours(0, 0, 0, 0)
      filtered = filtered.filter((task) => new Date(task.start_date) >= fromDate)
    }

    if (filters.toDate) {
      const toDate = new Date(filters.toDate)
      toDate.setHours(23, 59, 59, 999)
      filtered = filtered.filter((task) => new Date(task.end_date) <= toDate)
    }

    setFilteredTasks(filtered)
  }

  useEffect(() => {
    fetchAllProjectTasks()
    setAllUsers(User || [])
  }, [User])

  useEffect(() => {
    applyFilters(allTasks, activeTab)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, activeTab, allTasks])

  const handleTabChange = (tab) => {
    setActiveTab(tab)
  }

  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }))
  }

  const clearFilters = () => {
    setFilters({
      taskName: "",
      assignees: [],
      doers: [],
      fromDate: null,
      toDate: null,
      priority: "",
    })
  }

  const userOptions = allUsers.map((u) => ({ value: u.id, label: u.name }))

  return (
    <div className="card">
      <div className="card-body p-0">
        <div className="p-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div className="d-flex align-items-center gap-2">
              <h5 className="fs-5 my-1 mb-3">Project Dashboard</h5>
            </div>

            <button
              type="button"
              className="btn btn-exp-info btn-sm"
              onClick={() => setShowFilters((s) => !s)}
            >
              <i className="fas fa-filter me-2"></i>Filter
            </button>
          </div>

          <div className="mb-3 d-flex gap-2">
            <button
              className={`btn btn-sm ${activeTab === "pending" ? "btn-exp-primary" : "btn-outline-secondary"}`}
              onClick={() => handleTabChange("pending")}
            >
              <i className="fas fa-clock me-2"></i>Pending
            </button>
            <button
              className={`btn btn-sm ${activeTab === "inprogress" ? "btn-exp-primary" : "btn-outline-secondary"}`}
              onClick={() => handleTabChange("inprogress")}
            >
              <i className="fas fa-spinner me-2"></i>In Progress
            </button>
            <button
              className={`btn btn-sm ${activeTab === "complete" ? "btn-exp-primary" : "btn-outline-secondary"}`}
              onClick={() => handleTabChange("complete")}
            >
              <i className="fas fa-check-circle me-2"></i>Complete
            </button>
          </div>

          {/* Toggleable filter/search panel */}
          {showFilters && (
            <div className="filter-panel mb-3 p-3 bg-light rounded">
              <h6 className="mb-3 fw-bold">Search Task</h6>

              <div className="row g-3">
                {/* Task Name */}
                <div className="col-md-3">
                  <label className="form-label small fw-bold">Task Name</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Enter Task Name"
                    value={filters.taskName}
                    onChange={(e) => handleFilterChange("taskName", e.target.value)}
                  />
                </div>

                {/* Project Name */}
                <div className="col-md-3">
                  <label className="form-label small fw-bold">Project Name</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Enter Project Name"
                    value={filters.projectName || ""}
                    onChange={(e) => handleFilterChange("projectName", e.target.value)}
                  />
                </div>

                {/* Assignees (assigned_by) MultiSelect */}
                <div className="col-md-3">
                  <label className="form-label small fw-bold">Assignee (Assigned By)</label>
                  <Select
                    isMulti
                    options={userOptions}
                    value={userOptions.filter((o) => filters.assignees.includes(o.value))}
                    onChange={(selected) => handleFilterChange("assignees", selected ? selected.map((s) => s.value) : [])}
                    placeholder="Select..."
                    classNamePrefix="react-select"
                  />
                </div>

                {/* Doers MultiSelect (assigned_to) - uses separate component */}
                <div className="col-md-3">
                  <label className="form-label small fw-bold">Doers</label>
                  <DoerSelect
                    users={allUsers}
                    value={filters.doers}
                    onChange={(vals) => handleFilterChange("doers", vals)}
                    placeholder="Select doers..."
                  />
                </div>

                {/* From Date */}
                <div className="col-md-3">
                  <label className="form-label small fw-bold">From Date</label>
                  <DatePicker
                    selected={filters.fromDate}
                    onChange={(date) => handleFilterChange("fromDate", date)}
                    className="form-control form-control-sm"
                    placeholderText="Select start date"
                    dateFormat="yyyy-MM-dd"
                    isClearable
                  />
                </div>

                {/* To Date */}
                <div className="col-md-3">
                  <label className="form-label small fw-bold">To Date</label>
                  <DatePicker
                    selected={filters.toDate}
                    onChange={(date) => handleFilterChange("toDate", date)}
                    className="form-control form-control-sm"
                    placeholderText="Select end date"
                    dateFormat="yyyy-MM-dd"
                    isClearable
                  />
                </div>

                {/* Priority */}
                <div className="col-md-3">
                  <label className="form-label small fw-bold">Priority</label>
                  <select
                    className="form-control form-control-sm"
                    value={filters.priority}
                    onChange={(e) => handleFilterChange("priority", e.target.value)}
                  >
                    <option value="">Select...</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>

                {/* Clear Filters Button */}
                <div className="col-md-3 d-flex align-items-end">
                  <button className="btn btn-sm btn-outline-secondary w-100" onClick={clearFilters}>
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>
          )}

          <div style={{ height: "auto", overflow: "auto" }}>
            <Grid
              className="kendo-table-border-block rounded-10 overflow-auto"
              data={filteredTasks}
              sortable={{ mode: "multiple" }}
              pageable={{ pageSizes: [5, 10, 20], buttonCount: 3 }}
              resizable={true}
              loading={loading}
            >
              <GridColumn field="id" title="ID" width="70px" cell={SerialNumberCell} />
              <GridColumn field="project_name" title="Project Name" width="180px" cell={ProjectNameCell} />
              <GridColumn field="task_name" title="Task Name" width="180px" cell={TaskNameCell} />
              <GridColumn
                field="assigned_to"
                title="Doer"
                width="160px"
                cell={(props) => DoerNameCell(props, allUsers)}
              />
              <GridColumn
                field="assigned_by"
                title="Assign By"
                width="140px"
                cell={(props) => AssignByCell(props, allUsers)}
              />
              <GridColumn
                field="start_date"
                title="Start Date"
                width="130px"
                cell={(props) => DateCell(props, "start_date")}
              />
              <GridColumn
                field="end_date"
                title="End Date"
                width="130px"
                cell={(props) => DateCell(props, "end_date")}
              />
              <GridColumn field="priority" title="Priority" width="120px" cell={PriorityCell} />
              <GridColumn field="status" title="Status" width="130px" cell={StatusCell} />
              <GridColumn field="delay" title="Delay" width="140px" cell={DelayCell} />
              {/* <GridColumn field="complete_accuracy" title="Accuracy" width="120px" cell={AccuracyCell} /> */}
            </Grid>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProjectDashboard
